import { getSessionProgress } from 'graphql/queries';
import {
    insertSessionProgress as insertSessionProgressMutation,
    updateSessionProgress as updateSessionProgressMutation,
    closeAllQuizUserSessions as closeAllQuizUserSessionsMutation
} from 'graphql/mutations';

import {
    SessionProgressGet,
    SessionProgressGetVariables,
    SessionProgressGet_quiz_master_session_progress,
} from 'graphql/queries/types/SessionProgressGet';
import { InsertSessionProgress, InsertSessionProgressVariables } from 'graphql/mutations/types/InsertSessionProgress';
import { UpdateSessionProgress, UpdateSessionProgressVariables } from 'graphql/mutations/types/UpdateSessionProgress';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { CloseAllQuizUserSessions, CloseAllQuizUserSessionsVariables } from 'graphql/mutations/types/CloseAllQuizUserSessions';

interface SessionProgressActions {
    sessionProgress?: SessionProgressGet_quiz_master_session_progress;
    insertSessionProgress: (data: InsertSessionProgressVariables) => Promise<SessionProgressGet_quiz_master_session_progress | null>;
    updateSessionProgress: (data: UpdateSessionProgressVariables) => Promise<SessionProgressGet_quiz_master_session_progress | null>;
    closeAllQuizUserSessions: () => void;
}


export const useSessionProgress = (sessionId: number): SessionProgressActions => {
    const onGraphQLError = (error: ApolloError) => {
        console.error(error);
        // TODO - add user error message
    };

    const onSessionFetchComplete = (data: SessionProgressGet) => {
        if(!data.quiz_master_session_progress.at(0)) {
            insertSessionProgress({
                sessionId: sessionId,
                isPaused: false,
                progressIndex: -1
            })
        }
    }
    const { data: sessionProgress, refetch } = useQuery<SessionProgressGet, SessionProgressGetVariables>(getSessionProgress, {
        variables: { sessionId },
        onError: onGraphQLError,
        onCompleted: onSessionFetchComplete
    });

    const [insertSessionProgressFunc] = useMutation<InsertSessionProgress, InsertSessionProgressVariables>(insertSessionProgressMutation, {
        onError: onGraphQLError,
        refetchQueries: [getSessionProgress]
    });

    const [updateSessionProgressFunc] = useMutation<UpdateSessionProgress, UpdateSessionProgressVariables>(updateSessionProgressMutation, {
        onError: onGraphQLError,
        refetchQueries: [getSessionProgress]
    });

    const [closeAllQuizUserSessionsFunc] = useMutation<CloseAllQuizUserSessions, CloseAllQuizUserSessionsVariables>(closeAllQuizUserSessionsMutation, {
        onError: onGraphQLError
    });

    const insertSessionProgress = async (
        data: InsertSessionProgressVariables
    ): Promise<SessionProgressGet_quiz_master_session_progress | null> => {
        const resultData = await insertSessionProgressFunc({
            variables: data
        });

        if (resultData.errors) {
            return null;
        }

        refetch();

        return {
            id: resultData.data!.insert_quiz_master_session_progress_one!.id,
            isPaused: !!data.isPaused,
            progressIndex: data.progressIndex ?? null
        };
    };

    const updateSessionProgress = async (
        data: UpdateSessionProgressVariables
    ): Promise<SessionProgressGet_quiz_master_session_progress | null> => {
        const resultData = await updateSessionProgressFunc({
            variables: data
        });

        if (resultData.errors) {
            return null;
        }

        refetch();

        return {
            id: data.id,
            isPaused: !!resultData.data?.update_quiz_master_session_progress_by_pk?.isPaused,
            progressIndex: resultData.data?.update_quiz_master_session_progress_by_pk?.progressIndex ?? null
        };
    };

    const closeAllQuizUserSessions = () => {
        closeAllQuizUserSessionsFunc({
            variables: {
                sessionId: sessionId
            }
        })
    }

    return {
        sessionProgress: sessionProgress?.quiz_master_session_progress.at(0) ?? undefined,
        insertSessionProgress,
        updateSessionProgress,
        closeAllQuizUserSessions
    };
};
